/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { QoEAppLogger } from '../../../core/logging/qoe/QoEAppLogger';
import { RootState } from '../../../store/root.reducers';
import { MILLISECONDS_IN_A_SECOND } from '../../../config';
import { VideoManager } from '../../../core/videoplayer';

import './VideoDebugOverlay.scss';

const VideoDebugOverlay: FunctionComponent = (): JSX.Element => {
    const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
    const [, setTime] = useState(new Date());
    useEffect(() => {
        const interval = setInterval(() => {
            setTime(new Date());
        }, MILLISECONDS_IN_A_SECOND);

        return () => clearInterval(interval);
    }, []);

    const { overrideDetails } = useSelector((state: RootState) => state.appl);

    const { channelNumber, callSign } = useSelector(
        (state: RootState) => state.videoPlayer.channel,
    );

    const { tuningUri } = overrideDetails;

    let frameRate = '';
    let currentVarient = '';
    let makeVarient = '';
    let varients = '';

    const resolution = `${videoPlayerIns.getPlayerStats().height}p`;

    const data = new Map<string, string>();
    const channelCallSign = `${channelNumber} ${callSign}`;
    const connectionSpeedCheck: number = videoPlayerIns.getPlayerStats().estimatedBandwidth;
    const connectionSpeed: string = `${Math.round(connectionSpeedCheck / 1000)} kbps`;

    const getBitrate: number = videoPlayerIns.getPlayerStats().streamBandwidth;
    const bitrateUsed: string = `${Math.round(getBitrate / 1000)} kbps`;

    const codecAndDrm = `${videoPlayerIns.getAudioVideoCodec()},${videoPlayerIns.getDrmServerType()}`;

    const droppedAndDecodeFrames = `${videoPlayerIns.getPlayerStats().droppedFrames}/${
        videoPlayerIns.getPlayerStats().decodedFrames
    }`;

    const streamUrl = tuningUri || videoPlayerIns.getStreamingUrl();

    const currentTime: number = videoPlayerIns.getCurrentTimeInMs();
    const endTime: number = videoPlayerIns.getSeekRangeEndTimeInMs();

    if (endTime > currentTime) {
        QoEAppLogger.mediaStats.videoBufferAvailable = endTime - currentTime;
    }
    const getBufferAvailable = Math.round(QoEAppLogger.mediaStats.videoBufferAvailable / 60);
    const bufferAvailable: string = `${getBufferAvailable} S`;

    const variantGet = videoPlayerIns.getVideoVarients();
    const varientEntry = new Set<string>();
    variantGet.forEach((variant: any) => {
        if (`${variant.height}p` === resolution) {
            makeVarient = `{${variant.height}X${variant.width}@${variant.bandwidth / 1000} kbps}`;
            varientEntry.add(makeVarient);
            frameRate = variant.frameRate;
        } else {
            varientEntry.add(
                `{${variant.height}X${variant.width}@ ${variant.bandwidth / 1000} kbps}`,
            );
        }
    });
    varientEntry.forEach((item) => {
        if (item === makeVarient) {
            currentVarient = `{*${item}*}`;
        } else {
            varients += `{${item}} `;
        }
    });
    varients += currentVarient;

    const resolutionAndFrame = `${resolution},${frameRate}fps`;

    data.set('Channel/Asset ID', channelCallSign);
    data.set('Resolution/Rate', resolutionAndFrame);
    data.set('Connection Speed', connectionSpeed);
    data.set('Bitrate Used', bitrateUsed);
    data.set('Buffer Available', bufferAvailable);
    data.set('Dropped Frames', droppedAndDecodeFrames);
    data.set('Codec & DRM', codecAndDrm);
    data.set('Variants', `${varients}`);
    data.set('Streaming URL', streamUrl);

    return (
        <div className="video-debug-overlay-container">
            <div className="video-debug-overlay-key-value">
                {Array.from(data).map(([key, value]) => (
                    <div key={key}>
                        <span className="video-debug-overlay-key">{key} </span>
                        <span className="video-debug-overlay-value">{value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
export default VideoDebugOverlay;
